import { useState } from "react";

import { BodySmall, Box, Modal, ModalFooter, ModalHeader } from "@parspec/pixel";

import { CompanyObjType, type MergeCompanyRequest } from "../queries/apiTypes";
import RecordCheckboxCard from "../../shared/ResolveConflicts/RecordCheckboxCard";
import { useMergeCompanyRecordsMutation } from "../queries";
import { CRM_ENTITY_TYPE, MERGE_RECORDS_MODAL } from "../../shared/constant";

interface MergeRecordsModalProps {
    recordsPair: {
        record1: CompanyObjType,
        record2: CompanyObjType,
    };
    onClose: () => void;
    onSuccess: (message: string, updatedData: MergeCompanyRequest) => void;
}

const titleSXStyle = { lineHeight: '1.25rem' };

export default function MergeRecordsModal(props: MergeRecordsModalProps) {
    const { recordsPair, onClose, onSuccess } = props;
    const { record1, record2 } = recordsPair;

    const [primaryRecordId, setPrimaryRecordId] = useState(record1.id);

    const { isLoading: isMergingRecords, mutateAsync: mergeRecords } = useMergeCompanyRecordsMutation();

    const handleConfirmMergeRecords = async () => {
        if (isMergingRecords) return;

        const secondaryRecord = record1.id === primaryRecordId
            ? record2
            : record1;

        try {
            const payload: MergeCompanyRequest = {
                primary_company_id: primaryRecordId,
                secondary_company_id: secondaryRecord.id,
            }
            const response = await mergeRecords(payload);
            onSuccess(response.data.message, payload);
        } catch (err) { }
    };

    const handleCardClick = (selectedCardId: number) => setPrimaryRecordId(selectedCardId);

    const header = (
        <ModalHeader title="Merge Records" onClose={onClose}>
            <Box width={680}>
                <BodySmall color="secondary.light" limit={false} sx={titleSXStyle}>
                    {MERGE_RECORDS_MODAL.TITLE}
                </BodySmall>
            </Box>
        </ModalHeader>
    );

    const footer = (
        <ModalFooter
            onReject={onClose}
            onAccept={handleConfirmMergeRecords}
            continueButtonLabel="Confirm"
            cancelButtonLabel="Cancel"
            isLoading={isMergingRecords}
        />
    );

    return (
        <Modal header={header} footer={footer} open borderRadius="6px">
            <Box width={680} display="flex" gap={2}>
                {[record1, record2].map((record) => {
                    const { id } = record;
                    return (
                        <Box key={id} flex={1} flexShrink={0}>
                            <RecordCheckboxCard
                                id={id}
                                checked={id === primaryRecordId}
                                onCardClick={handleCardClick}
                                data={{ record, type: CRM_ENTITY_TYPE.COMPANY }}
                            />
                        </Box>
                    )
                })}
            </Box>
        </Modal>
    );
}